import ButtonTabs from "@components/ButtonTabs";
import React, {Dispatch, memo, ReactNode, SetStateAction, useMemo} from "react";
import cn from "classnames";
import {IconType} from "react-icons";
import {ID} from "@datassential/platform-ui-lib";
import {iButtonTabsItem} from "@components/ButtonTabs/ButtonTabs";
import {FCAdditionalProps} from "@datassential/platform-ui-lib/lib/core/models";

interface OutlinedTabsProps<T extends ID> extends FCAdditionalProps {
    selected: NonNullable<T>;
    setSelected: Dispatch<SetStateAction<NonNullable<T>>>;
    items: {
        id: NonNullable<T>;
        Icon: IconType;
        title: ReactNode;
    }[];
}

const OutlinedTabs = <T extends ID>({
    selected,
    setSelected,
    items,
    className,
}: OutlinedTabsProps<T>) => {
    const tabsData = useMemo(() => items.map(({
        id,
        title,
        Icon
    }) => ({
        id,
        title: (
            <div className="OutlinedTabs__tab-item">
                <Icon className="OutlinedTabs__tab-icon"/>
                <span>{title}</span>
            </div>
        ),
        buttonProps: {
            onClick: () => setSelected(id),
        }
    }) as iButtonTabsItem), [items, setSelected]);

    return (
        <ButtonTabs
            className={cn("OutlinedTabs", className)}
            data={tabsData}
            modifiers={["outlined"]}
            activeButtonModifiers={["white", "inherited-outline"]}
            defaultButtonModifiers={["lightgray"]}
            activeItemId={selected}
        />
    );
};

export default memo(OutlinedTabs) as <T extends ID>(props: OutlinedTabsProps<T>) => ReturnType<typeof OutlinedTabs>;
